import React from 'react';
import {
  FiArrowDown,
  FiArrowUp,
  FiMapPin,
  FiPhone,
  FiVideo,
} from 'react-icons/fi';

import StatusBadge from './StatusBadge';
import {
  formatCurrency,
  formatDate,
  formatRelative,
  formatTime,
  initials,
  titleCase,
} from '../../utils/format';

const MODALITY_ICON = { video: FiVideo, 'in-person': FiMapPin, phone: FiPhone };

const COLUMNS = [
  { key: 'reference', label: 'Ref', sortable: false },
  { key: 'customer', label: 'Client', sortable: true },
  { key: 'therapist', label: 'Therapist', sortable: true },
  { key: 'scheduledFor', label: 'Date & time', sortable: true },
  { key: 'modality', label: 'Format', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'fee', label: 'Fee', sortable: true, align: 'right' },
];

function SortHeader({ column, sort, order, onSort }) {
  if (!column.sortable) {
    return <th className={column.align === 'right' ? 'text-end' : undefined}>{column.label}</th>;
  }

  const active = sort === column.key;
  const nextOrder = active && order === 'asc' ? 'desc' : 'asc';

  return (
    <th
      className={column.align === 'right' ? 'text-end' : undefined}
      aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`mm-th-sort ${active ? 'mm-th-sort--active' : ''}`}
        onClick={() => onSort(column.key, nextOrder)}
      >
        {column.label}
        {active &&
          (order === 'asc' ? (
            <FiArrowUp size={12} aria-hidden="true" />
          ) : (
            <FiArrowDown size={12} aria-hidden="true" />
          ))}
      </button>
    </th>
  );
}

function RowSkeleton() {
  return (
    <tr className="mm-table__skeleton">
      {COLUMNS.map((c) => (
        <td key={c.key}>
          <span className="mm-skeleton__bar" style={{ margin: 0 }} />
        </td>
      ))}
    </tr>
  );
}

export default function AppointmentsTable({
  appointments = [],
  loading,
  sort,
  order,
  onSort,
  onSelect,
  selectedId,
}) {
  return (
    // Admin tables are wide by nature. Rather than hide columns on small
    // screens, the table scrolls inside its own container so the page body
    // never scrolls sideways.
    <div className="mm-table-wrap">
      <table className="mm-table">
        <caption className="mm-sr-only">
          Appointments, showing which client is booked with which therapist
        </caption>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <SortHeader
                key={column.key}
                column={column}
                sort={sort}
                order={order}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>

        <tbody>
          {loading &&
            Array.from({ length: 6 }, (_, i) => <RowSkeleton key={`sk-${i}`} />)}

          {!loading && appointments.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="mm-table__empty">
                <strong>No appointments match these filters.</strong>
                <span>Try widening the date range or clearing the search.</span>
              </td>
            </tr>
          )}

          {!loading &&
            appointments.map((a) => {
              const ModalityIcon = MODALITY_ICON[a.modality] ?? FiVideo;
              return (
                <tr
                  key={a.id}
                  className={`mm-table__row ${
                    selectedId === a.id ? 'mm-table__row--active' : ''
                  }`}
                  onClick={() => onSelect(a)}
                  // Rows are interactive, so they need to behave like buttons
                  // for anyone not using a mouse.
                  tabIndex={0}
                  role="button"
                  aria-label={`View appointment ${a.reference}: ${a.customer.name} with ${a.therapist.name}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(a);
                    }
                  }}
                >
                  <td className="mm-table__ref">{a.reference}</td>

                  <td>
                    <div className="mm-person">
                      <span className="mm-person__avatar" aria-hidden="true">
                        {initials(a.customer.name)}
                      </span>
                      <span className="mm-person__text">
                        <span className="mm-person__name">{a.customer.name}</span>
                        <span className="mm-person__sub">{a.customer.email}</span>
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="mm-person">
                      <img
                        className="mm-person__photo"
                        src={a.therapist.image}
                        alt=""
                        loading="lazy"
                        width={32}
                        height={32}
                      />
                      <span className="mm-person__text">
                        <span className="mm-person__name">{a.therapist.name}</span>
                        <span className="mm-person__sub">{a.therapist.title}</span>
                      </span>
                    </div>
                  </td>

                  <td>
                    <span className="mm-table__date">
                      {formatDate(a.scheduledFor)}
                    </span>
                    <span className="mm-table__sub">
                      {formatTime(a.scheduledFor)} &middot;{' '}
                      {formatRelative(a.scheduledFor)}
                    </span>
                  </td>

                  <td>
                    <span className="mm-modality">
                      <ModalityIcon size={13} aria-hidden="true" />
                      {titleCase(a.modality)}
                    </span>
                    <span className="mm-table__sub">{a.sessionType}</span>
                  </td>

                  <td>
                    <StatusBadge status={a.status} />
                  </td>

                  <td className="text-end mm-table__fee">
                    {formatCurrency(a.fee, a.currency)}
                    <span className="mm-table__sub">{a.durationMinutes} min</span>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
